/**
 * kult-consent.js — Consent-Bridge zur Shopify Customer Privacy API und Kult-Cookie-Banner
 * (docs/requirements/2026-10-03-analytics-konzept.md §3). Geladen von snippets/kult-analytics.liquid (defer).
 *
 * API: window.kultConsent = {
 *   ready(cb)          cb({analytics, marketing, preferences, api}) sobald der Zustand feststeht (sofort, wenn schon bekannt)
 *   allowed(category)  'analytics' | 'marketing' | 'preferences' → boolean
 *   declined(category) true, wenn der Besucher die Kategorie ausdrücklich abgelehnt hat (nur mit API)
 *   open()             Banner mit aufgeklappten Einstellungen öffnen (Footer „Cookie-Einstellungen“); false ohne API/Banner
 *   accept(kind)       'all' | 'necessary' | 'custom' (liest die Schalter) → speichert über setTrackingConsent
 * }
 * Event: document 'kult:consent' {analytics, marketing, preferences} bei jeder Änderung.
 * Ohne API (z. B. Vorschau ohne Kundendatenschutz): kein Banner, allowed('analytics') nur im Modus „always“.
 * Keine eigenen Cookies und kein eigener Speicher: der Zustand liegt in Shopify (_tracking_consent).
 */
(function () {
  'use strict';
  if (window.kultConsent) return;

  var cfg = {};
  try {
    var cfgEl = document.getElementById('kult-analytics-config');
    cfg = cfgEl ? JSON.parse(cfgEl.textContent || '{}') : {};
  } catch (e) { cfg = {}; }

  var isReady = false;
  var readyQueue = [];
  var last = null;
  var banner = null;
  var cssState = 0; /* 0 = nicht geladen, 1 = lädt, 2 = fertig */
  var reopened = false;
  var returnFocus = null;
  var CATS = ['analytics', 'marketing', 'preferences'];

  /* Eigene Kopie der Entscheidung (unbedingt erforderlich, um die Einwilligung zu merken): verhindert, dass das Banner
     erneut erscheint, wenn Shopify die Entscheidung nicht hält (Vorschau-Domain, blockiertes Cookie). 12 Monate gültig. */
  var STORE_KEY = 'kult:consent';
  function readStored() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || !d.t || Date.now() - d.t > 365 * 86400000) return null;
      return d;
    } catch (e) { return null; }
  }
  function writeStored(c) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, t: Date.now() })); } catch (e) { /* privater Modus */ }
  }

  function privacy() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    return p && typeof p.shouldShowBanner === 'function' ? p : null;
  }

  function allowed(category) {
    var p = privacy();
    if (!p) return category === 'analytics' && cfg.mode === 'always';
    var ok = false;
    try {
      if (category === 'analytics') ok = !!p.analyticsProcessingAllowed();
      if (category === 'marketing') ok = !!p.marketingAllowed();
      if (category === 'preferences') ok = !!p.preferencesProcessingAllowed();
    } catch (e) { /* API-Fehler: im Zweifel nein */ }
    if (ok) return true;
    /* Shopify kennt (noch) keine Entscheidung, wir aber schon: gemerkte Wahl gilt */
    var st = readStored();
    var open = false;
    try { open = !!p.shouldShowBanner(); } catch (e) { open = false; }
    return !!(st && open && st[category]);
  }

  function declined(category) {
    var p = privacy();
    if (!p || typeof p.currentVisitorConsent !== 'function') return false;
    try {
      var c = p.currentVisitorConsent() || {};
      return c[category] === 'no' || c[category] === false;
    } catch (e) { return false; }
  }

  function snapshot() {
    return { analytics: allowed('analytics'), marketing: allowed('marketing'), preferences: allowed('preferences') };
  }

  function emit() {
    var s = snapshot();
    if (last && last.analytics === s.analytics && last.marketing === s.marketing && last.preferences === s.preferences) return;
    last = s;
    try { document.dispatchEvent(new CustomEvent('kult:consent', { detail: s })); } catch (e) { /* alte Browser */ }
  }

  function shouldShowBanner() {
    var p = privacy();
    if (!p) return false;
    var need = false;
    try { need = !!p.shouldShowBanner(); } catch (e) { return false; }
    if (!need) return false;
    var st = readStored();
    if (!st) return true;
    /* Entscheidung liegt vor: nicht erneut fragen, sondern still an Shopify übergeben */
    try {
      p.setTrackingConsent({ analytics: !!st.analytics, marketing: !!st.marketing, preferences: !!st.preferences, sale_of_data: false }, function () { emit(); });
    } catch (e) { /* bleibt bei der gemerkten Wahl */ }
    return false;
  }

  function finish() {
    if (isReady) {
      /* API kam nach dem Timeout doch noch: Zustand nachziehen, Banner bei Bedarf zeigen */
      if (privacy()) { emit(); if (cfg.banner && shouldShowBanner()) show(false); }
      return;
    }
    isReady = true;
    last = snapshot();
    var state = { analytics: last.analytics, marketing: last.marketing, preferences: last.preferences, api: !!privacy() };
    readyQueue.splice(0).forEach(function (cb) { try { cb(state); } catch (e) { /* Aufrufer-Fehler nicht weitergeben */ } });
    if (cfg.banner && shouldShowBanner()) show(false);
  }

  function ready(cb) {
    if (typeof cb !== 'function') return;
    if (isReady) {
      try { cb({ analytics: allowed('analytics'), marketing: allowed('marketing'), preferences: allowed('preferences'), api: !!privacy() }); } catch (e) { /* ignorieren */ }
    } else {
      readyQueue.push(cb);
    }
  }

  /* ---------- Banner ---------- */
  function loadCss(done) {
    if (cssState === 2 || !cfg.css) { cssState = 2; done(); return; }
    var link = document.querySelector('link[data-kult-consent-css]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cfg.css;
      link.setAttribute('data-kult-consent-css', '');
      document.head.appendChild(link);
    }
    cssState = 1;
    var called = false;
    function once() { if (called) return; called = true; cssState = 2; done(); }
    link.addEventListener('load', once);
    link.addEventListener('error', once);
    window.setTimeout(once, 1500);
  }

  function build() {
    if (banner) return banner;
    var tpl = document.getElementById('kult-consent-template');
    if (!tpl || !tpl.content || !document.body) return null;
    var el = tpl.content.querySelector('[data-kult-consent]');
    if (!el) return null;
    banner = el.cloneNode(true);
    var skip = document.querySelector('body > .skip-to-content-link');
    if (skip && skip.nextSibling) document.body.insertBefore(banner, skip.nextSibling);
    else document.body.insertBefore(banner, document.body.firstChild);

    banner.addEventListener('click', function (event) {
      var acc = event.target.closest('[data-kult-consent-accept]');
      if (acc) { accept(acc.getAttribute('data-kult-consent-accept')); return; }
      if (event.target.closest('[data-kult-consent-save]')) { accept('custom'); return; }
      if (event.target.closest('[data-kult-consent-toggle]')) toggleSettings();
    });
    banner.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      /* Esc schließt nicht ohne Entscheidung: zuerst Einstellungen zuklappen, sonst nur bei erneutem Öffnen schließen */
      if (settingsOpen()) { toggleSettings(false); focusEl(banner.querySelector('[data-kult-consent-toggle]')); event.stopPropagation(); return; }
      if (reopened) { hide(); event.stopPropagation(); }
    });
    return banner;
  }

  function settingsOpen() {
    var panel = banner && banner.querySelector('[data-kult-consent-settings]');
    return !!(panel && !panel.hidden);
  }

  function syncSwitches() {
    if (!banner) return;
    CATS.forEach(function (cat) {
      var input = banner.querySelector('[data-kult-consent-category="' + cat + '"]');
      if (input) input.checked = allowed(cat);
    });
  }

  function toggleSettings(force) {
    if (!banner) return;
    var panel = banner.querySelector('[data-kult-consent-settings]');
    var btn = banner.querySelector('[data-kult-consent-toggle]');
    if (!panel || !btn) return;
    var open = typeof force === 'boolean' ? force : panel.hidden;
    if (open) syncSwitches();
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function show(isReopen) {
    if (!build()) return false;
    reopened = !!isReopen;
    if (isReopen) {
      returnFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
      toggleSettings(true);
    } else {
      toggleSettings(false);
    }
    loadCss(function () {
      banner.hidden = false;
      void banner.offsetWidth; /* Reflow statt rAF: Übergang startet auch in Hintergrund-Tabs */
      banner.classList.add('is-visible');
      if (isReopen) focusEl(banner.querySelector('#kult-consent-title'));
    });
    return true;
  }

  function hide() {
    if (!banner || banner.hidden) return;
    var hadFocus = banner.contains(document.activeElement);
    banner.classList.remove('is-visible');
    banner.hidden = true;
    if (hadFocus) {
      if (returnFocus && document.contains(returnFocus)) {
        focusEl(returnFocus);
      } else {
        var main = document.getElementById('MainContent');
        if (main) {
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
          focusEl(main);
        }
      }
    }
    returnFocus = null;
    reopened = false;
  }

  function readSwitches() {
    var c = {};
    CATS.forEach(function (cat) {
      var input = banner && banner.querySelector('[data-kult-consent-category="' + cat + '"]');
      c[cat] = !!(input && input.checked);
    });
    return c;
  }

  function accept(kind) {
    var c;
    if (kind === 'all') c = { analytics: true, marketing: true, preferences: true };
    else if (kind === 'necessary') c = { analytics: false, marketing: false, preferences: false };
    else if (kind && typeof kind === 'object') c = kind;
    else c = readSwitches();
    var p = privacy();
    writeStored(c);
    hide();
    if (!p || typeof p.setTrackingConsent !== 'function') return false;
    try {
      p.setTrackingConsent({
        analytics: !!c.analytics,
        marketing: !!c.marketing,
        preferences: !!c.preferences,
        sale_of_data: false
      }, function () { emit(); });
    } catch (e) { return false; }
    return true;
  }

  function open() {
    if (!isReady || !privacy() || !document.getElementById('kult-consent-template')) return false;
    return show(true);
  }

  window.kultConsent = { ready: ready, allowed: allowed, declined: declined, open: open, accept: accept };

  document.addEventListener('visitorConsentCollected', function () { emit(); });

  /* API laden: vorhanden → sofort; sonst über Shopify.loadFeatures (Timeout 5 s, danach ohne API weiter) */
  if (privacy()) {
    finish();
  } else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { finish(); });
    } catch (e) { finish(); }
    window.setTimeout(function () { if (!isReady) finish(); }, 5000);
  } else {
    finish();
  }
})();
